import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import moment from 'moment';

import CanPerform from 'components/shared/CanPerform';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import TextInput from 'components/ui/TextInput';
import Modal from 'components/ui/Modal/Modal';
import { PRR_REFUND } from 'constants/afterSalesShortcuts';
import { COMPENSATION_STATUS_COMPENSATED, COMPENSATION_STATUS_NOT_ENTITLED } from 'constants/compensation';
import { bookingShape } from 'constants/propTypes';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_SALESFORCE_TRAIN_DATA } from 'store/queries';
import GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS from 'store/queries/GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS';
import config from 'utils/config';

import { CtaButton, StyledButtonContainer } from '../AfterSalesCtas.styles';
import {
  allProductsHasNotBeenPrrRefunded,
  allSegmentsInOneDirection,
  allSegmentsInOneJourney,
  getSegmentsForItemRefs,
  runAfterSaleValidator,
  selectedProductsCheckedInOrConfirmedOrPrrAttempted,
  selectedProductsCheckedInOrPrrAttempted,
  areSomeExternalCarrierProductsSelected,
  hasDBTicketsSelected
} from '../AfterSalesCtas.utils';

import PrrAttemptedConfirmation from './PrrAttemptedConfirmation';
import {
  ManualOverrideContainer,
  StyledDateField,
  StyledInputContainer,
  StyledInputWrapper,
  StyledCalendar,
  StyledCalendarIcon,
  ErrorMessage
} from './PrrRefundCta.styles';
import PrrThroughFareConfirmation from './PrrThroughFareConfirmation';

const PrrRefundCta = ({
  booking,
  booking: { reference, outbound, inbound, allBookingItems },
  selectedItemRefs,
  disabled,
  isThroughFare
}) => {
  const client = useApolloClient();
  const history = useHistory();

  const enableSplitDelayCompensation = config('ENABLE_SPLIT_DELAY_COMPENSATION', { canOverride: true }) === 'true';

  const selectedLegs = useMemo(
    () => [...getSegmentsForItemRefs({ outbound, inbound }, selectedItemRefs)],
    [inbound, outbound, selectedItemRefs]
  );

  const isDirectOrSummated = selectedLegs.every(({ isThroughFare }) => !isThroughFare);
  const timingLegsSelected = selectedLegs.map(({ timing }) => timing.date);
  const allSelectedSegmentsDepartedMoreThan3MonthsAgo = timingLegsSelected.every((timing) => moment().diff(moment(timing), 'months') >= 3);

  const hasExternalCarriersSelected = areSomeExternalCarrierProductsSelected({
    selectedItemRefs,
    journeys: [...(booking.outbound || []), ...(booking.inbound || [])]
  });
  const isDBLegSelected = hasDBTicketsSelected({ segments: selectedLegs });
  const isDisabled =
    disabled ||
    (hasExternalCarriersSelected && !isThroughFare) ||
    isDBLegSelected ||
    !runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      allSegmentsInOneJourney,
      allSegmentsInOneDirection,
      allProductsHasNotBeenPrrRefunded,
      isThroughFare ? selectedProductsCheckedInOrConfirmedOrPrrAttempted : selectedProductsCheckedInOrPrrAttempted
    ]);

  const [getSalesforceTrainData, { error: salesforceTrainDataError, loading: salesforceTrainDataLoading, data: salesforceTrainData }] =
    useLazyQuery(GET_SALESFORCE_TRAIN_DATA, {
      onCompleted: ({ getSalesforceTrainData }) => {
        const { isCancelled } = getSalesforceTrainData ?? {};

        if (isCancelled) {
          handleThroughFareContinue(selectedItemRefs, { shouldUseOldFlow: true });
        }
      },
      onError: (error) => {
        console.error('Failed to get salesforce train data', error);
      }
    });

  const [loading, setLoading] = useState(false);
  const [showThroughFareConfirmation, setShowThroughFareConfirmation] = useState(false);
  const [delayWarning, setDelayWarning] = useState(false);
  const [prrAttemptedRecords, setPrrAttemptedRecords] = useState(null);
  const [compensationWarning, setCompensationWarning] = useState([]);
  const [calendarValue, setCalendarValue] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const membershipNumberUrl = membershipNumber ? `&membershipNumber=${membershipNumber}` : '';

  const handleContinue = useCallback(
    () => history.push(`/manage/bookings/${reference}/prr-refund?itemRefs=${encodeURIComponent(selectedItemRefs)}${membershipNumberUrl}`),
    [history, membershipNumberUrl, reference, selectedItemRefs]
  );

  const isAllTicketsPrrAttempted = useMemo(
    () =>
      selectedItemRefs.length > 0 && allBookingItems.length > 0
        ? allBookingItems.filter((p) => selectedItemRefs.includes(p.itemRef)).every((p) => p.isPrrAttempted)
        : false,
    [allBookingItems, selectedItemRefs]
  );

  const handleRouteToRefund = useCallback(async () => {
    if (!isThroughFare) {
      try {
        setLoading(true);
        setCalendarValue(selectedLegs[0]?.timing?.date);

        const ticketNumbers = selectedLegs
          .flatMap(({ products }) => products)
          .reduce((ticketNumbers, { ticketNumber, itemRef }) => {
            if (selectedItemRefs.includes(itemRef)) {
              ticketNumbers.push(ticketNumber);
            }

            return ticketNumbers;
          }, []);

        const { data } = await client.query({
          query: GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS,
          variables: {
            ticketNumbers,
            bookingReference: reference
          }
        });

        const getExtendedCompensationByTicketNumbers = data?.getExtendedCompensationByTicketNumbers ?? [];

        const ticketsWithoutCompensation = ticketNumbers.reduce((tickets, ticketNumber) => {
          const compensationRecord = getExtendedCompensationByTicketNumbers.find(
            (compensation) => compensation.ticketNumber === ticketNumber
          );

          if (
            !compensationRecord ||
            [COMPENSATION_STATUS_COMPENSATED, COMPENSATION_STATUS_NOT_ENTITLED].includes(compensationRecord.status)
          ) {
            tickets.push(ticketNumber);
          }

          return tickets;
        }, []);

        if (ticketsWithoutCompensation.length > 0) {
          setCompensationWarning(ticketsWithoutCompensation);

          return;
        }

        if (isAllTicketsPrrAttempted) {
          setPrrAttemptedRecords(getExtendedCompensationByTicketNumbers);

          return;
        }

        if (isDirectOrSummated && allSelectedSegmentsDepartedMoreThan3MonthsAgo) {
          setDelayWarning(true);

          return;
        }
      } catch (error) {
        throw new AgentPortalError(error);
      } finally {
        setLoading(false);
      }
    }

    handleContinue();
  }, [
    client,
    handleContinue,
    isAllTicketsPrrAttempted,
    isThroughFare,
    reference,
    selectedItemRefs,
    selectedLegs,
    allSelectedSegmentsDepartedMoreThan3MonthsAgo,
    isDirectOrSummated
  ]);

  const handleThroughFareContinue = (selectedItems, state) =>
    history.push(`/manage/bookings/${reference}/prr-refund?itemRefs=${encodeURIComponent(selectedItems)}${membershipNumberUrl}`, {
      ...state
    });

  const handleCloseCompensationWarning = () => setCompensationWarning([]);
  const handleShowThroughFareConfirmation = () => setShowThroughFareConfirmation(true);
  const handleClosePrrAttemptedWarning = () => setPrrAttemptedRecords(null);

  const handleManualOverride = (event) => {
    event.preventDefault();

    const trainDate = event.target['train-date']?.value;
    const trainNumber = event.target['train-number']?.value;
    const departureDate = moment(trainDate, 'DD-MM-YYYY').format('YYYY-MM-DD');

    getSalesforceTrainData({
      variables: {
        trainNumber,
        departureDate
      }
    });
  };

  const trainNumberHandlers = {
    onKeyDown: (event) => {
      if (event?.keyCode === 69) {
        event?.preventDefault();
      }
    },
    onInput: ({ target }) => {
      if (target?.value?.length > 4) {
        target.value = target.value.slice(0, 4);
      }
    }
  };

  return (
    <CanPerform afterSalesShortcut={PRR_REFUND}>
      {(isEligible) =>
        isEligible && (
          <>
            <CtaButton
              data-cy="prr-refund-cta"
              label={<FormattedMessage id="afterSales.prrRefund" />}
              disabled={isDisabled}
              loading={loading}
              onClick={enableSplitDelayCompensation && isThroughFare ? handleShowThroughFareConfirmation : handleRouteToRefund}
            />
            {enableSplitDelayCompensation ? (
              <PrrThroughFareConfirmation
                isOpen={showThroughFareConfirmation}
                selectedLegs={selectedLegs}
                selectedItemRefs={selectedItemRefs}
                onContinue={handleThroughFareContinue}
                onClose={() => setShowThroughFareConfirmation(false)}
              />
            ) : null}
            {isAllTicketsPrrAttempted ? (
              <PrrAttemptedConfirmation
                compensationRecords={prrAttemptedRecords}
                onClose={handleClosePrrAttemptedWarning}
                onContinue={handleContinue}
              />
            ) : null}
            {!isThroughFare ? (
              compensationWarning.length === 0 ? (
                <Modal
                  isOpen={delayWarning}
                  onClose={() => setDelayWarning(false)}
                  title={<FormattedMessage id="prrRefund.compensationWarningTitle" />}
                  subtitle={<FormattedMessage id="prrRefund.delayWaningSubtitle" />}
                >
                  <StyledButtonContainer>
                    <Button
                      data-cy="prr-refund-cta-delay-continue"
                      label={<FormattedMessage id="exchanges.continue" />}
                      disabled={isDisabled}
                      loading={loading}
                      onClick={handleContinue}
                    />
                  </StyledButtonContainer>
                  <Button
                    data-cy="prr-refund-cta-delay-close"
                    label={<FormattedMessage id="extras.cancel" />}
                    disabled={isDisabled}
                    loading={loading}
                    onClick={() => setDelayWarning(false)}
                  />
                </Modal>
              ) : (
                <Modal
                  isOpen={compensationWarning.length > 0}
                  onClose={handleCloseCompensationWarning}
                  title={<FormattedMessage id="prrRefund.compensationWarningTitle" />}
                  subtitle={
                    <>
                      <div>
                        <FormattedMessage id="prrRefund.delayWaningSubtitleBis" />
                      </div>
                      <FormattedMessage id="prrRefund.compensationWarningSubTitle" values={{ tickets: compensationWarning.join(', ') }} />
                    </>
                  }
                >
                  <ManualOverrideContainer
                    data-testid="manual-override-container"
                    data-cy="manual-override-container"
                    onSubmit={handleManualOverride}
                  >
                    <FormattedMessage id="prrRefund.manualOverrideCompensation" />:
                    <StyledInputContainer>
                      <StyledInputWrapper>
                        <FormattedMessage id="prrRefund.trainNumber" />:
                        <Input
                          id="train-number"
                          name="train-number"
                          data-cy="manual-override-train-number-input"
                          data-testid="manual-override-train-number-input"
                          type="number"
                          placeHolder="9999"
                          required
                          {...trainNumberHandlers}
                        />
                      </StyledInputWrapper>
                      <StyledInputWrapper isCalendar>
                        <FormattedMessage id="prrRefund.trainDate" />:
                        <StyledDateField htmlFor="train-date" onClick={() => setCalendarOpen(!calendarOpen)}>
                          <TextInput
                            type="text"
                            readOnly
                            id="train-date"
                            value={calendarValue && moment(calendarValue).format('DD-MM-YYYY')}
                            placeholder="DD-MM-YYYY"
                            required
                          />
                          <StyledCalendarIcon size={20} />
                        </StyledDateField>
                        {calendarOpen && (
                          <StyledCalendar
                            showNeighboringMonth={false}
                            tabIndex="-1"
                            onChange={(e) => {
                              setCalendarValue(e);
                              setCalendarOpen(false);
                            }}
                          />
                        )}
                      </StyledInputWrapper>
                    </StyledInputContainer>
                    <Button
                      data-cy="manual-override-button"
                      data-testid="manual-override-button"
                      type="submit"
                      loading={salesforceTrainDataLoading}
                      disabled={salesforceTrainDataLoading}
                    >
                      <FormattedMessage id="prrRefund.manualOverride" />
                    </Button>
                    {salesforceTrainData?.getSalesforceTrainData?.isCancelled === false && (
                      <ErrorMessage
                        id="error-not-cancelled-train"
                        data-testid="error-not-cancelled-train"
                        data-cy="error-not-cancelled-train"
                      >
                        <FormattedMessage id="prrRefund.manualOverrideError" />
                      </ErrorMessage>
                    )}
                    {salesforceTrainDataError && (
                      <ErrorMessage id="error-lastName" data-testid="error-not-cancelled-train" data-cy="error-not-cancelled-train">
                        <FormattedMessage id="genericError.somethingWrong" />
                      </ErrorMessage>
                    )}
                  </ManualOverrideContainer>
                </Modal>
              )
            ) : null}
          </>
        )
      }
    </CanPerform>
  );
};

PrrRefundCta.propTypes = {
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  isThroughFare: PropTypes.bool
};

export default PrrRefundCta;
